// Conditional formatting — parity with insight-bi's ADV_FORMAT, which it feeds
// to Flexmonster/WebDataRocks as `addCondition({formula, measure, format})`.
//
// ADV_FORMAT is a string of one or more `"<formula>~<json>"` rules joined by
// `$`, e.g.  "#value# > 100~{\"backgroundColor\":\"#fde\"}$#value# < 0~{...}".
// The JSON is a Flexmonster format object (camelCase CSS-ish keys). WebDataRocks
// consumes the same shape, so the Pivot passes the parsed conditions straight
// through; ag-grid has no formula engine, so the Grid evaluates them here.

import type { CSSProperties } from 'react'

export interface FmtCondition {
  formula: string
  format: Record<string, string>
}

/** Parse an ADV_FORMAT string into its conditions (malformed parts skipped). */
export function parseAdvFormat(adv?: string | null): FmtCondition[] {
  if (!adv) return []
  const out: FmtCondition[] = []
  for (const part of String(adv).split('$')) {
    const i = part.indexOf('~')
    if (i < 0) continue
    const formula = part.slice(0, i).trim()
    try {
      const format = JSON.parse(part.slice(i + 1))
      if (formula && format && typeof format === 'object') out.push({ formula, format })
    } catch {
      /* skip a malformed rule rather than break the whole column */
    }
  }
  return out
}

// Evaluate a Flexmonster-style numeric formula: `#value#` comparisons joined by
// && / || (single level — no nested parens, which covers the common cases).
export function evalFormula(formula: string, value: number): boolean {
  try {
    const expr = formula.replace(/#value#/gi, String(value))
    return expr.split('||').some((or) => or.split('&&').every(compare))
  } catch {
    return false
  }
}

function compare(expr: string): boolean {
  const m = expr.match(/^\s*(-?\d+(?:\.\d+)?)\s*(>=|<=|==|!=|<>|=|>|<)\s*(-?\d+(?:\.\d+)?)\s*$/)
  if (!m) return false
  const x = parseFloat(m[1])
  const y = parseFloat(m[3])
  switch (m[2]) {
    case '>': return x > y
    case '<': return x < y
    case '>=': return x >= y
    case '<=': return x <= y
    case '==':
    case '=': return x === y
    case '!=':
    case '<>': return x !== y
    default: return false
  }
}

// Flexmonster format keys → React CSS properties.
const STYLE_KEYS: Record<string, keyof CSSProperties> = {
  backgroundColor: 'backgroundColor',
  color: 'color',
  fontFamily: 'fontFamily',
  fontSize: 'fontSize',
  fontWeight: 'fontWeight',
  fontStyle: 'fontStyle',
  textAlign: 'textAlign',
}

/** Map a Flexmonster format object to a React style object (known keys only). */
export function formatToStyle(format: Record<string, string>): CSSProperties {
  const style: Record<string, string> = {}
  for (const [k, prop] of Object.entries(STYLE_KEYS)) {
    if (format[k] != null) style[prop as string] = String(format[k])
  }
  return style as CSSProperties
}

/** Merged style for whichever conditions match a numeric cell value (or null). */
export function conditionalStyle(conditions: FmtCondition[], value: unknown): CSSProperties | null {
  if (!conditions.length) return null
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return null
  let style: CSSProperties | null = null
  for (const c of conditions) {
    if (evalFormula(c.formula, n)) style = { ...(style || {}), ...formatToStyle(c.format) }
  }
  return style
}
